/**
 * AtlasGravesModule — the land keeps its dead (the generations wave).
 *
 * Renders the burial grounds NEAR the player from the replicated `grave`
 * table: a small headstone + slab per grave, batched into ONE draw call via
 * StaticInstancer, seated on the rendered terrain (meshHeight — the same
 * grid the avatar clamp uses, so markers never float or sink).
 *
 * THE RING (2026-09-12 perf sweep): graves are epochal rows (one per death,
 * forever) — 4,324 of them across 312 settlements by now, and the sweep used
 * to place EVERY one on world entry: two analytic terrain samples each, far
 * from the cached chunk ring, 1.25 s on the main thread in the entry profile
 * — and past 2,048 graves the 4,096-instance pool was full, so half the
 * world's yards silently never appeared. Now only graves within RING_M of
 * the player stand, at most BATCH per pass, re-swept every SWEEP_MS or when
 * the player has moved RESWEEP_M; graves that fall out of the ring are
 * unplaced so the pool never fills.
 *
 * Instances carry WORLD coordinates under the (possibly origin-rebased) scene
 * root — the same space terrain chunks render in.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { StaticInstancer } from '../lib/instancing.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { cemeterySpot } from '../lib/hinterland.js';
import { settlementSites } from '../../../shared/settlements.js';

interface GraveRow { settlementId: bigint; graveId: bigint; worldId: bigint; x: number; z: number; name: string; cause: string }

const RING_M = 1500;        // graves stand within this radius of the player
const UNPLACE_M = 1900;     // …and come down beyond this one (hysteresis)
const BATCH = 48;           // placements per pass (2 terrain samples each)
const SWEEP_MS = 1500;
const RESWEEP_M = 250;
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _s = new THREE.Vector3(1, 1, 1);
const _up = new THREE.Vector3(0, 1, 0);

export class AtlasGravesModule implements IModule {
  readonly name = 'AtlasGraves';

  private scene!: THREE.Scene;
  private instancer: StaticInstancer | null = null;
  private headstone = new THREE.BoxGeometry(0.5, 0.85, 0.12);
  private slab = new THREE.BoxGeometry(0.7, 0.12, 1.3);
  private stone = new THREE.MeshStandardMaterial({ color: 0x8d8f96, roughness: 0.95, metalness: 0 });
  private placed = new Map<string, { handles: number[]; x: number; z: number }>(); // graveId → instance handles + position
  private wired = false;
  private subscribed = false;
  private timer: number | null = null;
  private sweepX = Infinity;
  private sweepZ = Infinity;
  private pending = false;   // a pass hit BATCH — more graves wait inside the ring

  init(app: App): void {
    this.scene = app.world.scene;
    this.instancer = new StaticInstancer(this.scene, 4096, 'graves');
    bus.on('atlas:account-ready', () => this.wire());
    // World transitions clear the yard THEN re-seat it from the cache —
    // onInsert never refires for rows already replicated, so a clear alone
    // left every burial ground empty for the rest of the session.
    // world-cleared fires BEFORE atlas.currentWorldId changes (and it is null
    // on first entry), so sweeping here placed the PREVIOUS world's graves and
    // nothing on first entry. Clear on cleared, re-seat on entered.
    bus.on('atlas:world-cleared', () => { this.clear(); });
    bus.on('atlas:world-entered', () => { this.clear(); this.sweep(true); });
    this.timer = window.setInterval(() => this.tick(), SWEEP_MS);
  }

  /** Re-sweep when the player has moved, or while a pass left graves waiting. */
  private tick(): void {
    if (atlas.currentWorldId === null) return;
    if (this.pending || Math.hypot(atlas.localX - this.sweepX, atlas.localZ - this.sweepZ) > RESWEEP_M) this.sweep(false);
  }

  private wire(): void {
    const conn = atlas.conn;
    if (!conn || this.wired) return;
    this.wired = true;
    conn.db.grave.onInsert((_ctx, row) => { const g = row as unknown as GraveRow; if (Math.hypot(g.x - atlas.localX, g.z - atlas.localZ) <= RING_M) this.place(conn, g, this.commonsSeed(conn)); });
    if (!this.subscribed) {
      this.subscribed = true;
      conn.subscriptionBuilder().onApplied(() => this.sweep(true)).subscribe(['SELECT * FROM grave']);
    }
  }

  /**
   * Stand the graves inside the ring that are not standing yet (at most
   * BATCH per pass — the rest wait for the next tick), take down the ones
   * that drifted out. Idempotent.
   */
  private sweep(fresh: boolean): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    const px = atlas.localX, pz = atlas.localZ;
    this.sweepX = px; this.sweepZ = pz;
    if (fresh) this.pending = false;
    // Out of the ring: unplace (hysteresis keeps a walker at the edge from flickering yards).
    for (const [key, p] of this.placed) {
      if (Math.hypot(p.x - px, p.z - pz) > UNPLACE_M) { for (const h of p.handles) this.instancer?.remove(h); this.placed.delete(key); }
    }
    const seed = this.commonsSeed(conn);
    if (!seed) return;
    let budget = BATCH;
    let waiting = false;
    for (const row of conn.db.grave.iter()) {
      const g = row as unknown as GraveRow;
      if (g.worldId !== atlas.currentWorldId) continue;
      if (Math.hypot(g.x - px, g.z - pz) > RING_M) continue;
      if (this.placed.has(g.graveId.toString())) continue;
      if (budget <= 0) { waiting = true; break; }
      this.place(conn, g, seed);
      budget--;
    }
    this.pending = waiting;
  }

  private commonsSeed(conn: DbConnection): string | null {
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private place(conn: DbConnection, g: GraveRow, seed: string | null = this.commonsSeed(conn)): void {
    if (!this.instancer) return;
    const key = g.graveId.toString();
    if (this.placed.has(key)) return;
    // A null currentWorldId is NOT "any world" — pre-entry (the main menu)
    // nothing places (the menu-time streaming bug family, 2026-07-30).
    if (atlas.currentWorldId === null || g.worldId !== atlas.currentWorldId) return;
    if (!seed) return;
    // The headstone faces down its row toward the gate: three's yaw is −the precinct's standard-frame
    // yaw (THE HANDEDNESS LAW), plus a hair of jitter. A grave outside any precinct (the legacy yard)
    // keeps the old world-Z facing.
    _q.setFromAxisAngle(_up, this.precinctYaw(seed, g.settlementId) + ((Number(g.graveId) * 0.61803) % 0.24) - 0.12);
    const handles: number[] = [];
    // Ground each piece at ITS OWN position (high-relief audit 2026-08-05):
    // one shared Y sampled at g.z visibly split headstone from slab on slopes
    // (they sit 0.7 m apart in Z — a 0.3 slope is a 20 cm step between them).
    const yHead = meshHeight(seed, g.x, g.z - 0.55);
    const ySlab = meshHeight(seed, g.x, g.z + 0.15);
    _m.compose(_v.set(g.x, yHead + 0.42, g.z - 0.55), _q, _s);
    handles.push(this.instancer.add(this.headstone, this.stone, _m));
    _m.compose(_v.set(g.x, ySlab + 0.06, g.z + 0.15), _q, _s);
    handles.push(this.instancer.add(this.slab, this.stone, _m));
    this.placed.set(key, { handles: handles.filter((h) => h >= 0), x: g.x, z: g.z });
  }

  private yawCache = new Map<string, number>();
  private precinctYaw(seed: string, settlementId: bigint): number {
    const key = `${seed}|${settlementId}`;
    let y = this.yawCache.get(key);
    if (y === undefined) {
      const site = settlementSites(seed).find((s) => s.id === Number(settlementId) - 1);
      const spot = site ? cemeterySpot(seed, site) : null;
      y = spot ? -spot.yaw : 0;
      this.yawCache.set(key, y);
    }
    return y;
  }

  private clear(): void {
    if (!this.instancer) return;
    for (const p of this.placed.values()) for (const h of p.handles) this.instancer.remove(h);
    this.placed.clear();
    this.pending = false;
  }

  dispose(): void {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    this.clear();
    this.instancer?.dispose();
    this.headstone.dispose();
    this.slab.dispose();
    this.stone.dispose();
  }
}
